import {
  deleteSCWebHook,
  getAllSCWebHooks,
  SalesCenterWebhook,
  selectAllSCWebHooks,
  selectSCWebHookFilters,
  setSelectedWebHook,
  updateSCWebHook,
} from '@user/store/scWebHooks'
import { useSelector } from 'react-redux'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import { toast } from '@sales-center/components'
import { updateLoadingState } from '@sales-center/common'
import { useDialogState } from '@user/utils/useDialogState'

export const useData = () => {
  const allSCWebHooks = useSelector(selectAllSCWebHooks)
  const dispatch = useAppDispatch()
  const { orderBy, page, perPage } = useSelector(selectSCWebHookFilters)
  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'fail'>(
    'idle',
  )

  const [sideSheetOpen, setSideSheetOpen] = useState<boolean>(false)

  const [cancelDialogOpen, showCancelDialog, hideCancelDialog] =
    useDialogState()

  useEffect(() => {
    let mounted = true

    const execute = async () => {
      try {
        setStatus('pending')
        dispatch(updateLoadingState('visible'))
        const result = await dispatch(getAllSCWebHooks())

        if (mounted) {
          if (getAllSCWebHooks.fulfilled.match(result)) {
            setStatus('success')
          } else {
            setStatus('fail')
            toast.error(`Error loading webhook list`)
          }
        }
      } catch (error) {
        if (mounted) {
          setStatus('fail')
        }
      } finally {
        dispatch(updateLoadingState('hidden'))
      }
    }
    execute()

    return () => {
      mounted = false
    }
  }, [dispatch])

  const paginatedRows = useMemo(() => {
    const sortedRows = allSCWebHooks.sort((a, b) => {
      const [field, direction] = orderBy.split(' ')
      const order = direction === 'desc' ? -1 : 1
      return a[field] > b[field] ? order : -order
    })
    const startIndex = (page - 1) * perPage
    return sortedRows.slice(startIndex, startIndex + perPage)
  }, [allSCWebHooks, orderBy, page, perPage])

  const deleteWebhook = useCallback(
    async (webHookId: number) => {
      try {
        dispatch(updateLoadingState('visible'))
        const result = await dispatch(deleteSCWebHook(String(webHookId)))
        if (deleteSCWebHook.fulfilled.match(result)) {
          toast.success(`Webhook deleted successfully`)
        } else {
          toast.error('Error deleting webhook')
        }
      } finally {
        dispatch(updateLoadingState('hidden'))
      }
    },
    [dispatch],
  )

  const handleSideSheetClose = useCallback(() => {
    setSideSheetOpen(false)
  }, [])

  const handleCreateEditWebhooks = useCallback(
    (webhook?: SalesCenterWebhook) => {
      if (webhook || paginatedRows.length < 10) {
        setSideSheetOpen(true)
        dispatch(setSelectedWebHook(webhook))
      } else {
        showCancelDialog()
      }
    },
    [dispatch, paginatedRows.length, showCancelDialog],
  )

  const onChangeWebHookStatus = useCallback(
    async (webhook: SalesCenterWebhook) => {
      try {
        dispatch(updateLoadingState('visible'))
        const result = await dispatch(
          updateSCWebHook({
            ...webhook,
            isEnabled: !webhook.isEnabled,
          }),
        )
        if (updateSCWebHook.fulfilled.match(result)) {
          toast.success(`Webhook updated successfully`)
        } else {
          toast.error('Error updating webhook')
        }
      } finally {
        dispatch(updateLoadingState('hidden'))
      }
    },
    [dispatch],
  )

  return {
    allSCWebHooks: paginatedRows,
    webhookLoading: status === 'pending',
    orderBy,
    page,
    perPage,
    deleteWebhook,
    sideSheetOpen,
    handleSideSheetClose,
    handleCreateEditWebhooks,
    onChangeWebHookStatus,
    cancelDialogOpen,
    hideCancelDialog,
  }
}
